import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Button, Dropdown, notification, Popconfirm } from "antd";
import {
  LockOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { BsThreeDotsVertical } from "react-icons/bs";
import GenericStatCard from "../../../../Components/Cards/GenericStatCard";
import creditIcon from "../../../../Assets/Icons/garage_money.svg";
import paymentIcon from "../../../../Assets/Icons/payments.svg";
import grayPaymentIcon from "../../../../Assets/Icons/gray_payments.svg";
import humididtyIcon from "../../../../Assets/Icons/humidity_mid.svg";
import grayFuelIcon from "../../../../Assets/Icons/gray_fuel_icon.svg";
import dateIcon from "../../../../Assets/Icons/calendar_month.svg";
import regIcon from "../../../../Assets/Icons/markdown_copy.svg";
import currencyIcon from "../../../../Assets/Icons/attach_money.svg";
import contactIcon from "../../../../Assets/Icons/account_child_invert.svg";
import mailIcon from "../../../../Assets/Icons/mail.svg";
import locIcon from "../../../../Assets/Icons/distance.svg";
import logo from "../../../../Assets/Icons/Avatarprofile.svg";
import GenericTable from "../../../../Components/Tables/GenericTable";
import docIcon from "../../../../Assets/Icons/document.svg";
import pdfIcon from "../../../../Assets/Icons/pdf.svg";
import dataUsageIcon from "../../../../Assets/Icons/data_usage.svg";
import laptopCarIcon from "../../../../Assets/Icons/laptop_car.svg";
import creditCardIcon from "../../../../Assets/Icons/credit_card_gear_white.svg";
import GenericModal from "../../../../Components/Modals/GenericModal";
import pdfFile from "../../../../Assets/Files/file-sample.pdf";
import docxFile from "../../../../Assets/Files/file-sample.docx";

import "./style.css";
import BarChart from "../../../../Components/Charts/BarChart";
import { getCompanyCreditDetails } from "../../../../API/Admin/CreditsBillings";
import { updateFleetCompanyFuelingStatus } from "../../../../API/Admin/FleetManagement";
import usePageAccess from "../../../../Utlis/usePageAccess";
import { getInitials } from "../../../../Components/InitialsAvatar";

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  let path = avatar.startsWith("/") ? avatar : `/${avatar}`;
  if (!path.startsWith("/media/")) path = `/media${path}`;
  return `${BACKEND_ORIGIN}${path}`;
};

const ProfileAvatar = ({ src, name }) =>
  src ? (
    <img
      src={src}
      alt={name}
      className="profile-avatar"
      style={{ objectFit: "cover" }}
    />
  ) : (
    <div className="profile-avatar">
      {(() => {
        const parts = (name || "-").trim().split(/\s+/);
        const first = parts[0].charAt(0);
        const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
        return `${first}${last}`.toUpperCase();
      })()}
    </div>
  );

const formatCurrency = (value) => `$${(parseFloat(value) || 0).toFixed(2)}`;

const DriverAvatar = ({ src, name }) =>
  src ? (
    <img
      src={src}
      alt={name}
      style={{
        width: 24,
        height: 24,
        borderRadius: "5px",
        objectFit: "cover",
      }}
    />
  ) : (
    <span
      style={{
        width: 24,
        height: 24,
        borderRadius: "5px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
        fontWeight: 700,
        color: "#fff",
        background: getAvatarColor(name),
        flexShrink: 0,
      }}
    >
      {getInitials(name)}
    </span>
  );

const FleetCreditDetails = () => {
  const { canEdit } = usePageAccess("credits-billing");
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedRows, setSelectedRows] = useState([]);
  const [isFuelModalOpen, setIsFuelModalOpen] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [reason, setReason] = useState("");
  const [companyData, setCompanyData] = useState(null);

  const companyId = location.state?.companyId;

  useEffect(() => {
    if (!companyId) return;

    const fetchCompanyCredits = async () => {
      try {
        const res = await getCompanyCreditDetails(companyId);

        if (Math.floor(res?.status / 100) === 2) {
          const data = res?.data?.data || null;

          setCompanyData(data);
          setIsLocked(!!data?.fueling_locked);
          setReason(data?.fueling_locked_reason || "");
        }
      } catch (err) {
        console.error("Failed to load company credit details:", err);
      }
    };

    fetchCompanyCredits();
  }, [companyId]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleCreditAdjustments = () => {
    navigate("/credit-adjustments", { state: { companyId } });
  };

  const handleToggleFueling = () => {
    if (!isLocked) {
      setIsFuelModalOpen(true);
    }
  };

  const handleConfirmLock = async (enteredReason) => {
    if (!companyId) return;

    try {
      const res = await updateFleetCompanyFuelingStatus(companyId, {
        fueling_locked: true,
        reason: enteredReason,
      });

      if (Math.floor(res?.status / 100) === 2) {
        setIsLocked(true);
        setReason(enteredReason);
        setIsFuelModalOpen(false);

        notification.success({
          message: "Fuel Locked",
          description: "Fueling has been locked successfully!",
          placement: "topRight",
        });
      } else {
        notification.error({
          message: "Action Failed",
          description: res?.data?.message || "Failed to lock fueling.",
          placement: "topRight",
        });
      }
    } catch (err) {
      notification.error({
        message: "Action Failed",
        description:
          err?.response?.data?.message || "Failed to lock fueling.",
        placement: "topRight",
      });
    }
  };

  const handleEnableFueling = async () => {
    if (!companyId) return;

    try {
      const res = await updateFleetCompanyFuelingStatus(companyId, {
        fueling_locked: false,
      });

      if (Math.floor(res?.status / 100) === 2) {
        setIsLocked(false);
        setReason("");

        notification.success({
          message: "Fuel Enabled",
          description: "Fueling has been enabled successfully!",
          placement: "topRight",
        });
      } else {
        notification.error({
          message: "Action Failed",
          description: res?.data?.message || "Failed to enable fueling.",
          placement: "topRight",
        });
      }
    } catch (err) {
      notification.error({
        message: "Action Failed",
        description:
          err?.response?.data?.message || "Failed to enable fueling.",
        placement: "topRight",
      });
    }
  };

  const columns = [
    {
      title: "Driver",
      dataIndex: "driver",
      key: "driver",
      enableSearch: true,
      render: (text, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <DriverAvatar src={record.logo} name={text} />
          {text}
        </span>
      ),
    },
    {
      title: "Vehicle",
      dataIndex: "vehicle",
      key: "vehicle",
      enableSearch: true,
      render: (text) => <span className="assigned-module-badge">{text}</span>,
    },
    {
      title: "Usage Progress",
      dataIndex: "progress",
      key: "progress",
      sorter: (a, b) => {
        if (a.progress !== b.progress) {
          return a.progress - b.progress;
        }
      },
      render: (progress) => {
        let color = "#16a34a";
        if (progress > 80) {
          color = "#dc2626";
        } else if (progress > 50) {
          color = "#f59e0b";
        }

        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div className="progress-bar" style={{ flex: 1 }}>
              <div
                className="progress-fill"
                style={{
                  width: `${progress}%`,
                  backgroundColor: color,
                }}
              />
            </div>
            <span style={{ fontSize: 12, minWidth: 35 }}>{progress}%</span>
          </div>
        );
      },
    },
    {
      title: "Limit",
      dataIndex: "limit",
      key: "limit",
      sorter: (a, b) =>
        parseFloat(a.limit.replace(/[^0-9.-]+/g, "")) -
        parseFloat(b.limit.replace(/[^0-9.-]+/g, "")),
      render: (text) => <span className="creidt-amount">{text}</span>,
    },
  ];

  const creditAllocations = (companyData?.vehicle_allocations || []).map(
    (allocation) => ({
      key: String(allocation.vehicle_id),
      driver: allocation.driver || "-",
      logo: resolveAvatarUrl(allocation.driver_avatar),
      vehicle: allocation.vehicle || "-",
      licensePlate: allocation.license_plate || "-",
      progress: allocation.usage_percent || 0,
      limit: formatCurrency(allocation.amount),
    }),
  );

  const capitalize = (value) =>
    value ? value.charAt(0).toUpperCase() + value.slice(1) : "-";

  const recentTransactions = (companyData?.recent_transactions || []).map(
    (txn) => ({
      key: String(txn.id),
      date: [txn.date, txn.time].filter(Boolean).join(" ") || "-",
      createdAt: txn.created_at,
      transactionId: txn.txn_id || "-",
      type: capitalize(txn.source),
      vehicle: txn.vehicle_name || "-",
      driver: txn.driver_name || "-",
      driverAvatar: resolveAvatarUrl(txn.driver_avatar),
      amount: formatCurrency(txn.amount),
      balance:
        txn.remaining_balance != null
          ? formatCurrency(txn.remaining_balance)
          : "-",
      status: capitalize(txn.status),
    }),
  );

  const getUniqueFilters = (data, key) => {
    const uniqueValues = [...new Set(data.map((item) => item[key]))];
    return uniqueValues.map((val) => ({ text: val, value: val }));
  };
  const recentTransactionsColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) =>
        (new Date(a.createdAt).getTime() || 0) -
        (new Date(b.createdAt).getTime() || 0),
    },
    {
      title: "Transaction ID",
      dataIndex: "transactionId",
      key: "transactionId",
      enableSearch: true,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      filters: getUniqueFilters(recentTransactions, "type"),
      onFilter: (value, record) => record.type === value,
      render: (text) => <span className="creidt-amount">{text}</span>,
    },
    {
      title: "Vehicle",
      dataIndex: "vehicle",
      key: "vehicle",
      enableSearch: true,
    },
    {
      title: "Driver",
      dataIndex: "driver",
      key: "driver",
      render: (text, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <DriverAvatar src={record.driverAvatar} name={text} />
          {text}
        </span>
      ),
      enableSearch: true,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sorter: (a, b) =>
        parseFloat(String(a.amount ?? "0").replace(/[^0-9.-]+/g, "")) -
        parseFloat(String(b.amount ?? "0").replace(/[^0-9.-]+/g, "")),
      render: (text) => {
        return <span>{text}</span>;
      },
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      sorter: (a, b) =>
        parseFloat(String(a.balance ?? "0").replace(/[^0-9.-]+/g, "")) -
        parseFloat(String(b.balance ?? "0").replace(/[^0-9.-]+/g, "")),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: getUniqueFilters(recentTransactions, "status"),
      onFilter: (value, record) => record.status === value,
      render: (text) => (
        <span
          className={`status-badge ${
            text === "Approved" ? "approved" : "denied"
          }`}
        >
          {text}
        </span>
      ),
    },
  ];

  return (
    <div className="fleet-company-details">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back
        </Button>
      </div>
      <div className="header-wrapper">
        <div className="title">{companyData?.legal_name || "-"}</div>
        <div className="btns">
          {canEdit && (
            <div>
              {!isLocked ? (
                <Button
                  className="fuel-btn"
                  icon={<LockOutlined />}
                  onClick={handleToggleFueling}
                >
                  Lock Fueling
                </Button>
              ) : (
                <Popconfirm
                  title="Are you sure you want to enable fueling?"
                  onConfirm={handleEnableFueling}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    className="fuel-btn enabled"
                    icon={<LockOutlined />}
                    style={{
                      color: "#16a34a",
                      backgroundColor: "#16a34a1a",
                    }}
                  >
                    Fueling Enabled
                  </Button>
                </Popconfirm>
              )}
            </div>
          )}

          {canEdit && (
            <div>
              <Button
                type="primary"
                className="edit-btn"
                onClick={handleCreditAdjustments}
              >
                <img
                  className="button-icon"
                  src={creditCardIcon}
                  alt="Credit Card Icon"
                />
                Manage Credits
              </Button>
            </div>
          )}
        </div>
      </div>
      {isLocked && (
        <div className="locked-info">
          <div className="label">
            Suspension Reason
            <div className="reason"> {reason}</div>
          </div>
        </div>
      )}
      <GenericModal
        open={isFuelModalOpen}
        title="Confirm Fueling Lock"
        subtitle={`Are you sure you want to lock fueling of ${companyData?.legal_name || "-"}? This will immediately decline all active card transactions.`}
        highlightText={companyData?.legal_name || "-"}
        textareaLabel="Reason"
        textareaPlaceholder="Enter reason..."
        confirmText="Confirm Lock"
        cancelText="Cancel"
        iconColor="#ef4444"
        iconBgColor="#ef44441a"
        confirmBtnColor="#EA580C"
        onCancel={() => setIsFuelModalOpen(false)}
        onConfirm={handleConfirmLock}
      />

      <div className="row">
        <div className="left-column">
          <div className="profile-card">
            <div className="profile-header">
              <ProfileAvatar
                src={resolveAvatarUrl(companyData?.avatar)}
                name={companyData?.legal_name}
              />
            </div>

            <div className="profile-body">
              <div className="company-name">
                {companyData?.legal_name || "-"}
              </div>
              <div className="company-id">ID: {companyData?.id ?? "-"}</div>

              <div
                style={{ display: "flex", gap: 8, justifyContent: "center" }}
              >
                <span
                  className={`badge ${isLocked ? "badge-fuel" : "badge-active"}`}
                >
                  Fueling Status: {isLocked ? "Inactive" : "Active"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="right-column">
          <div className="tabs">
            <GenericStatCard
              title="Total Credit Balance"
              value={formatCurrency(companyData?.total_balance)}
              icon={creditIcon}
            />
          </div>
          <div className="tabs">
            <GenericStatCard
              title="Allocated"
              value={formatCurrency(companyData?.allocated_total)}
              icon={dataUsageIcon}
            />
          </div>
          <div className="tabs">
            <GenericStatCard
              title="Available"
              value={formatCurrency(companyData?.available_balance)}
              icon={paymentIcon}
            />
          </div>
          <div className="tabs">
            <GenericStatCard
              title="Pending Top-Ups"
              value={formatCurrency(companyData?.pending_topups_total)}
              icon={laptopCarIcon}
              showBlankLine="false"
            />
          </div>
        </div>
      </div>

      <div className="document-table">
        <GenericTable
          title="Credit Allocation"
          dataSource={creditAllocations}
          columns={columns}
          onSelectionChange={(keys, rows) => setSelectedRows(rows)}
          onRowClick={(record) => console.log("Row clicked:", record)}
        />
      </div>
      <div className="document-table">
        <GenericTable
          title="Recent Transactions"
          dataSource={recentTransactions}
          columns={recentTransactionsColumns}
        />
      </div>
    </div>
  );
};

export default FleetCreditDetails;
